import Decimal from "decimal.js-light";

import {
  INCHES_PER_FOOT,
  LINEAL_MM_DECIMAL_PLACES,
  SQ_FT_DISPLAY_DECIMALS,
  SQ_FT_STORAGE_DECIMALS,
} from "./constant";

/**
 * FOR SQUARE FEET - Pricing Strategy
 * Calculates the total area in square feet from length and width in feet and inches.
 * This is used when working with US area measurements in square feet.
 * Rounds to display precision (4 decimals) for UX consistency.
 * Use in calculating total area of a given length and width entry
 *
 * @param {Object} [params={}] - The dimensions object
 * @param {number} [params.lengthFt=0] - Length in feet
 * @param {number} [params.lengthIn=0] - Length in inches
 * @param {number} [params.widthFt=0] - Width in feet
 * @param {number} [params.widthIn=0] - Width in inches
 * @param {number} [params.amount=1] - Quantity multiplier
 * @returns {number} The area in square feet (4 decimal places for display)
 */
export const calculateTotalAreaInSquareFeet = ({
  lengthFt = 0,
  lengthIn = 0,
  widthFt = 0,
  widthIn = 0,
  amount = 1,
} = {}) => {
  const totalLengthFeet = Decimal(lengthFt).plus(Decimal(lengthIn).dividedBy(INCHES_PER_FOOT));
  const totalWidthFeet = Decimal(widthFt).plus(Decimal(widthIn).dividedBy(INCHES_PER_FOOT));
  const area = totalLengthFeet.times(totalWidthFeet);

  return parseFloat(Decimal(amount).times(area).toDecimalPlaces(SQ_FT_DISPLAY_DECIMALS));
};

/**
 * * FOR LINEAL FEET - Pricing Strategy
 * Calculates the total linear length in millimeters from a lengths of an item
 * Returns both the actual total and a "forPrice" total that applies minimum length requirements.
 * This is used for US measurements where the canonical DB unit is millimeters.
 *
 * @param {Object} params - The parameters object
 * @param {Array<{amount?: number, length?: number}>} params.items - Array of lengths with amount and length properties
 * @param {number} [params.minLengthInMm=0] - Minimum length in millimeters to use for pricing calculations
 * @returns {{total: number, forPrice: number}} An object with total (actual) and forPrice (with minLength applied) in millimeters (4 decimal places)
 * @throws {Error} Throws an error if items is not an array
 */
export const sumTotalLinealFeetInMm = ({ items, minLengthInMm = 0 }) => {
  const arr = Array.isArray(items) ? items : [];

  const result = arr.reduce(
    (acc, item) => {
      const amount = Decimal(item?.amount ?? 1);
      const lengthMm = Decimal(item?.lengthMm ?? item?.length ?? 0);

      const itemTotalMm = amount.times(lengthMm);
      const lengthForPrice = lengthMm.lt(minLengthInMm) ? minLengthInMm : lengthMm;
      const itemForPrice = amount.times(lengthForPrice);

      return {
        total: acc.total.plus(itemTotalMm),
        forPrice: acc.forPrice.plus(itemForPrice),
      };
    },
    { total: Decimal(0), forPrice: Decimal(0) }
  );

  return {
    total: result.total.toDecimalPlaces(LINEAL_MM_DECIMAL_PLACES),
    forPrice: result.forPrice.toDecimalPlaces(LINEAL_MM_DECIMAL_PLACES),
  };
};

/**
 * FOR SQUARE FEET - Pricing Strategy
 * Sums total area in square feet from a list of dimensions.
 * Items must have totalAreaInSquareFeet. Aggregates each item's area (4 decimals) for UX: visible items = visible total.
 *
 * @param {Object} params
 * @param {Array<{totalAreaInSquareFeet?: number}>} [params.items=[]] - Array of items with totalAreaInSquareFeet
 * @returns {{total: number}} Total area in square feet (4 decimal places)
 */
export const sumTotalAreaOfSquareFeetInSquareFeet = ({ items = [] } = {}) => {
  const arr = Array.isArray(items) ? items : [];

  const total = arr.reduce(
    (acc, item) =>
      acc.plus(Decimal(item?.totalAreaInSquareFeet ?? 0).toDecimalPlaces(SQ_FT_DISPLAY_DECIMALS)),
    Decimal(0)
  );
  return {
    total: total.toDecimalPlaces(SQ_FT_DISPLAY_DECIMALS),
  };
};

/**
 * FOR SQUARE FEET - Pricing Strategy
 * Sums total area in square meters from a list of dimensions.
 * Items must have totalAreaInSquareMeters. Aggregates each item's area (6 decimals) for storage.
 *
 * @param {Object} params
 * @param {Array<{totalAreaInSquareMeters?: number}>} [params.items=[]] - Array of items with totalAreaInSquareMeters
 * @returns {{total: number}} Total area in square meters (6 decimal places)
 */
export const sumTotalAreaOfSquareFeetInSquareMeters = ({ items = [] } = {}) => {
  const arr = Array.isArray(items) ? items : [];
  const total = arr.reduce(
    (acc, item) =>
      acc.plus(Decimal(item?.totalAreaInSquareMeters ?? 0).toDecimalPlaces(SQ_FT_STORAGE_DECIMALS)),
    Decimal(0)
  );

  return {
    total: total.toDecimalPlaces(SQ_FT_STORAGE_DECIMALS),
  };
};

/**
 * FOR LINEAL METRES - Pricing Strategy
 * Calculates total lineal length in mm from items with amount and length (in mm).
 * Skips items with isDeleted. Optionally applies a minimum length for the forPrice total.
 * Uses item.tempUid ? item.length : item to read amount and length per item.
 *
 * @param {Object} params
 * @param {Array<{amount?: number, length?: number, isDeleted?: boolean, tempUid?: boolean, length?: object}>} [params.items=[]] - Array of dimension items (length in mm)
 * @param {number} [params.minLengthInMm=0] - Minimum length in mm to use for the forPrice total
 * @returns {{ total: number, forPrice: number }} total and forPrice in millimeters (4 decimal places)
 */
export const sumTotalLinealMetersLengthInMm = ({ items = [], minLengthInMm = 0 } = {}) => {
  const arr = Array.isArray(items) ? items : [];

  const result = arr.reduce(
    (acc, item) => {
      if (item?.isDeleted) {
        return acc;
      }
      // const source = item?.tempUid ? item?.length : item;
      const amount = Decimal(item?.amount ?? 1);
      const lengthMm = Decimal(item?.length ?? 0);
      const lengthForPrice = lengthMm.lt(minLengthInMm) ? minLengthInMm : lengthMm;
      const itemTotalMm = amount.times(lengthMm);
      const itemForPriceMm = amount.times(lengthForPrice);
      return {
        total: acc.total.plus(itemTotalMm),
        forPrice: acc.forPrice.plus(itemForPriceMm),
      };
    },
    { total: Decimal(0), forPrice: Decimal(0) }
  );

  return {
    total: result.total.toDecimalPlaces(LINEAL_MM_DECIMAL_PLACES),
    forPrice: result.forPrice.toDecimalPlaces(LINEAL_MM_DECIMAL_PLACES),
  };
};
/**
 * FOR LINEAL METRES - Pricing Strategy
 * Calculates total lineal length in metres from items with amount and length (in mm).
 * Optionally applies a minimum length for a "forPrice" total (e.g. for pricing when short pieces are charged at min length).
 *
 * @param {Object} params
 * @param {Array<{amount?: number, length?: number}>} [params.items=[]] - Array of dimension items (length in mm)
 * @param {number} [params.minLengthInMm=0] - Minimum length in mm to use for the forPrice total
 * @returns {{ total: Decimal, forPrice: Decimal }} total = actual sum in metres; forPrice = sum with minLength applied (3 decimal places)
 */
export const sumTotalLinealMetersLengthInMetres = ({ items = [], minLengthInMm = 0 } = {}) => {
  const arr = Array.isArray(items) ? items : [];
  const minLength = minLengthInMm ?? 0;
  const { total, forPrice } = arr.reduce(
    (acc, item) => {
      if (item?.isDeleted) {
        return acc;
      }
      const amount = Decimal(item?.amount ?? 1);
      const length = item?.length ?? 0;
      const lengthForPrice = minLength > length ? minLength : length;
      const totalLength = amount.times(length);
      const forPriceLength = amount.times(lengthForPrice);
      return {
        total: acc.total.plus(totalLength),
        forPrice: acc.forPrice.plus(forPriceLength),
      };
    },
    { total: Decimal(0), forPrice: Decimal(0) }
  );
  return {
    total: total.dividedBy(1000).toDecimalPlaces(LINEAL_MM_DECIMAL_PLACES),
    forPrice: forPrice.dividedBy(1000).toDecimalPlaces(LINEAL_MM_DECIMAL_PLACES),
  };
};

/**
 * Calculates total area in square metres from items with length and width (in metres).
 * FOR SQUARE METRES - Pricing Strategy
 * @param {Object} params
 * @param {Array<{amount?: number, length?: number, width?: number}>} [params.items=[]] - Array of dimension items (amount defaults to 1, length and width in metres)
 * @returns {{ total: Decimal }} Total area in square metres (4 decimal places)
 */
export const sumTotalAreaOfSquareMetersInSquareMeters = ({ items = [] } = {}) => {
  const DECIMAL_PLACES = 4;
  const arr = Array.isArray(items) ? items : [];
  const total = arr.reduce((acc, item) => {
    const amount = Decimal(item?.amount ?? 1);
    const area = Decimal(item?.length ?? 0).times(Decimal(item?.width ?? 0));
    return acc.plus(amount.times(area));
  }, Decimal(0));

  return {
    total: total.toDecimalPlaces(DECIMAL_PLACES),
  };
};

export const sumTotalCustomFormulaQuantity = ({ quantity = 0, formulaOutput = 0 } = {}) => {
  const qty = Decimal(quantity);
  const output = Decimal(formulaOutput);
  return qty.times(output).toDecimalPlaces(4);
};
